import * as React from 'react';
import { SerializedData } from 'bernie-core';
import { observer } from 'mobx-react-lite';

import { ActivityDestinationViewSearch } from '../../../common/__generated__/api';
import { CheckboxTracked } from '../../../utils/click-trackers';

import { useStores } from '../../../utils/hooks';
import { checkboxClickHandler, SEE_ALL_CATEGORY, seeAllCheckboxClickHandler } from './filter-helper';
import Fields = ActivityDestinationViewSearch.Fields;
import Options = ActivityDestinationViewSearch.Options;
import NestedOptions = ActivityDestinationViewSearch.NestedOptions;

export interface FilterProps {
  inDialog?: boolean;
  field: Fields;
}

const replacePipeFromSubcategory = (name: string): string => {
  return name.replace('|', '.');
};

export const getCheckboxKeyAndId = (key: string): SerializedData => {
  const [, filterKey, filterCategoryId] = key.split('-');

  return { filterKey, filterCategoryId };
};

export const Checkbox: React.FC<FilterProps> = observer(({ inDialog, field }) => {
  const { activitySearchStore, routeStore } = useStores();
  const categoryName = field.label || undefined;
  const categoryId = field.id;
  const filterOptions = activitySearchStore.sortAndFilterObject.filters;

  function updateCheckbox(event: React.ChangeEvent<HTMLInputElement>): void {
    const { filterKey, filterCategoryId } = getCheckboxKeyAndId(event.target.id);
    if (filterCategoryId === SEE_ALL_CATEGORY) {
      seeAllCheckboxClickHandler(filterKey, filterCategoryId, activitySearchStore, routeStore, event, inDialog);
    } else {
      checkboxClickHandler(filterKey, filterCategoryId, activitySearchStore, routeStore, event, inDialog);
    }
  }

  const renderSubCategories = (
    checked: boolean,
    categoryId: string,
    subCategoryTitle: string,
    subFilterProps?: NestedOptions[] | null
  ): JSX.Element | null => {
    if (!checked || !subFilterProps) {
      return null;
    }

    return (
      <fieldset className="all-l-padding-four all-l-margin-four" data-testid="filter--subcategory-options">
        <legend className="is-visually-hidden">{subCategoryTitle}</legend>
        {subFilterProps.map(subProps => (
          <CheckboxTracked
            key={`child-category-${subProps.value}`}
            id={`cb-${subProps.value}-${categoryId}`}
            label={subProps.primary}
            value={filterOptions[categoryId][subProps.value].selected}
            checked={filterOptions[categoryId][subProps.value].selected}
            disabled={!filterOptions[categoryId][subProps.value].enabled}
            onChange={updateCheckbox}
            rfrr={
              subProps.analytics?.referrerId ||
              (filterOptions[categoryId][subProps.value]
                ? 'LX.SR.FO.Category'
                : `LX.SR.filterSort.Category.${replacePipeFromSubcategory(subProps.value)}`)
            }
            linkName={subProps.analytics?.linkName}
            data-testid="filter--subcategory-options-checkbox"
          />
        ))}
      </fieldset>
    );
  };

  return (
    <>
      <fieldset key={categoryName}>
        {categoryName ? (
          <legend
            className="all-t-padding-four all-b-padding-two uitk-type-heading-400 uitk-type-bold"
            data-testid="filter--category-name"
          >
            {categoryName}
          </legend>
        ) : null}
        {(field as ActivityDestinationViewSearch.ActivitySelectionFieldInlineFragment).options.map(
          (option: Options) => (
            <div key={`parent-category-${option.value}`} data-testid={`filter--${categoryId}-options`}>
              <CheckboxTracked
                id={`cb-${option.value}-${categoryId}`}
                label={option.primary}
                description={option.secondary}
                value={filterOptions[categoryId][option.value].selected}
                checked={filterOptions[categoryId][option.value].selected}
                disabled={!filterOptions[categoryId][option.value].enabled}
                onChange={updateCheckbox}
                rfrr={
                  option.analytics?.referrerId ||
                  (filterOptions[categoryId][option.value]
                    ? 'LX.SR.FO.Category'
                    : `LX.SR.filterSort.Category.${replacePipeFromSubcategory(option.value)}`)
                }
                linkName={option.analytics?.linkName}
                data-testid={`filter--${categoryId}-options-checkbox`}
              />
              {renderSubCategories(
                filterOptions[categoryId][option.value].selected,
                categoryId,
                option.primary,
                option.nestedOptions
              )}
            </div>
          )
        )}
      </fieldset>
    </>
  );
});
